import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { FileText, Clock, Calendar } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';
import { formatDate } from '../../utils/dateTimeUtils';

const LateFeeReport = () => {
  const [lateFees, setLateFees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => { fetchLateFees(); }, []);

  const fetchLateFees = async () => {
    setLoading(true);
    try {
      const allLateFees = [];
      
      // 1. Get fee payments with late fee
      try {
        const feePaymentsRes = await apiClient.get('/api/fees/payments');
        const payments = Array.isArray(feePaymentsRes.data) ? feePaymentsRes.data : [];
        const withLateFee = payments.filter(p => (p.late_fee || 0) > 0 || (p.fine_amount || 0) > 0);
        withLateFee.forEach(p => {
          allLateFees.push({
            id: p.id,
            student_name: p.student_name,
            source: 'Fee Payment',
            reason: p.fee_type || 'Late Payment',
            date: p.payment_date,
            fee_amount: p.amount || 0,
            late_fee: p.late_fee || p.fine_amount || 0,
            status: 'Paid',
            payment_mode: p.payment_mode
          });
        });
      } catch (e) { console.log('Fee payments not available'); }
      
      // 2. Get hostel fines (late fees)
      try {
        const hostelFinesRes = await apiClient.get('/api/hostel/fines');
        const hostelFines = Array.isArray(hostelFinesRes.data) ? hostelFinesRes.data : [];
        const lateFines = hostelFines.filter(f => 
          f.fine_reason?.toLowerCase().includes('late') || 
          f.fine_type?.toLowerCase().includes('late')
        );
        lateFines.forEach(f => {
          allLateFees.push({
            id: f.id,
            student_name: f.student_name,
            source: 'Hostel Fine',
            reason: f.fine_reason || 'Late Fee',
            date: f.fine_date,
            fee_amount: 0,
            late_fee: f.fine_amount || 0,
            status: f.payment_status || 'Unpaid',
            payment_mode: f.payment_mode
          });
        });
      } catch (e) { console.log('Hostel fines not available'); }
      
      // 3. Get transport fines (if exists)
      try {
        const transportFinesRes = await apiClient.get('/api/transport/fines');
        const transportFines = Array.isArray(transportFinesRes.data) ? transportFinesRes.data : [];
        const lateFines = transportFines.filter(f => 
          f.fine_reason?.toLowerCase().includes('late') || 
          f.fine_type?.toLowerCase().includes('late')
        );
        lateFines.forEach(f => {
          allLateFees.push({
            id: f.id,
            student_name: f.student_name,
            source: 'Transport Fine',
            reason: f.fine_reason || 'Late Fee',
            date: f.fine_date,
            fee_amount: 0,
            late_fee: f.fine_amount || 0,
            status: f.payment_status || 'Unpaid',
            payment_mode: f.payment_mode
          });
        });
      } catch (e) { console.log('Transport fines not available'); }
      
      // 4. Get library fines (late returns)
      try {
        const libraryFinesRes = await apiClient.get('/api/library/fines');
        const libraryFines = Array.isArray(libraryFinesRes.data) ? libraryFinesRes.data : [];
        const lateFines = libraryFines.filter(f => 
          f.fine_reason?.toLowerCase().includes('late') || 
          f.fine_type?.toLowerCase().includes('late') ||
          f.fine_reason?.toLowerCase().includes('overdue')
        );
        lateFines.forEach(f => {
          allLateFees.push({
            id: f.id,
            student_name: f.student_name || f.member_name,
            source: 'Library Fine',
            reason: f.fine_reason || 'Late Return',
            date: f.fine_date || f.created_at,
            fee_amount: 0,
            late_fee: f.fine_amount || f.amount || 0,
            status: f.payment_status || (f.paid ? 'Paid' : 'Unpaid'),
            payment_mode: f.payment_mode
          });
        });
      } catch (e) { console.log('Library fines not available'); }
      
      // Sort by date descending
      allLateFees.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
      
      setLateFees(allLateFees);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter data
  const getFilteredData = () => {
    let filtered = [...lateFees];
    
    if (statusFilter && statusFilter !== 'all') {
      filtered = filtered.filter(f => f.status === statusFilter);
    }
    
    if (startDate) {
      filtered = filtered.filter(f => f.date >= startDate);
    }
    if (endDate) {
      filtered = filtered.filter(f => f.date <= endDate);
    }
    
    return filtered;
  };

  const filteredFees = getFilteredData();
  
  // Calculate summary
  const totalLateFee = filteredFees.reduce((sum, f) => sum + (f.late_fee || 0), 0);
  const collectedFees = filteredFees.filter(f => f.status === 'Paid');
  const collected = collectedFees.reduce((sum, f) => sum + (f.late_fee || 0), 0);
  const pending = totalLateFee - collected;

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-orange-600" />
            Late Fee Report
          </CardTitle>
          <div className="flex gap-2 items-center flex-wrap">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Paid">Paid</SelectItem>
                <SelectItem value="Unpaid">Unpaid</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <Input 
                type="date" 
                value={startDate} 
                onChange={(e) => setStartDate(e.target.value)} 
                className="w-36"
              />
              <span className="text-gray-500">to</span>
              <Input 
                type="date" 
                value={endDate} 
                onChange={(e) => setEndDate(e.target.value)} 
                className="w-36"
              />
            </div>
            <Button onClick={fetchLateFees} disabled={loading}>
              <FileText className="w-4 h-4 mr-2" />{loading ? 'Loading...' : 'Generate'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <p className="text-sm text-orange-600">Total Late Fees</p>
              <p className="text-2xl font-bold text-orange-700">{filteredFees.length}</p>
            </div>
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-600">Total Amount</p>
              <p className="text-2xl font-bold text-red-700">₹{totalLateFee.toLocaleString('en-IN')}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-green-600">Collected</p>
              <p className="text-2xl font-bold text-green-700">₹{collected.toLocaleString('en-IN')}</p>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-700">₹{pending.toLocaleString('en-IN')}</p>
            </div>
          </div>

          <div className="text-sm text-gray-500 mb-2">
            Showing {filteredFees.length} late fee records
          </div>

          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-3">Student</th>
                <th className="text-left p-3">Source</th>
                <th className="text-left p-3">Reason</th>
                <th className="text-left p-3">Date</th>
                <th className="text-right p-3">Late Fee</th>
                <th className="text-center p-3">Payment Mode</th>
                <th className="text-center p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredFees.length === 0 ? (
                <tr><td colSpan="7" className="text-center p-8 text-gray-500">No late fee records found.</td></tr>
              ) : (
                filteredFees.map((fee, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="p-3 font-medium">{fee.student_name || '-'}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        fee.source === 'Fee Payment' ? 'bg-blue-100 text-blue-800' :
                        fee.source === 'Hostel Fine' ? 'bg-purple-100 text-purple-800' :
                        fee.source === 'Transport Fine' ? 'bg-orange-100 text-orange-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {fee.source}
                      </span>
                    </td>
                    <td className="p-3">{fee.reason}</td>
                    <td className="p-3">{fee.date ? formatDate(fee.date) : '-'}</td>
                    <td className="p-3 text-right text-orange-600 font-semibold">₹{(fee.late_fee || 0).toLocaleString('en-IN')}</td>
                    <td className="p-3 text-center text-sm text-gray-600">{fee.payment_mode || '-'}</td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        fee.status === 'Paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {fee.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Summary by Source */}
          {filteredFees.length > 0 && (
            <div className="mt-6 border-t pt-4">
              <h3 className="font-semibold mb-3">Summary by Source</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {['Fee Payment', 'Hostel Fine', 'Transport Fine', 'Library Fine'].map(source => {
                  const sourceFees = filteredFees.filter(f => f.source === source);
                  const sourceTotal = sourceFees.reduce((s, f) => s + (f.late_fee || 0), 0);
                  const sourcePaid = sourceFees.filter(f => f.status === 'Paid').reduce((s, f) => s + (f.late_fee || 0), 0);
                  if (sourceFees.length === 0) return null;
                  return (
                    <div key={source} className="p-3 bg-gray-50 rounded-lg text-sm">
                      <p className="font-medium">{source}</p>
                      <p className="text-gray-600">Count: {sourceFees.length}</p>
                      <p className="text-red-600">Total: ₹{sourceTotal.toLocaleString('en-IN')}</p>
                      <p className="text-green-600">Paid: ₹{sourcePaid.toLocaleString('en-IN')}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LateFeeReport;
